/**
 * Matrice delle preferenze di notifica (F-101): righe gli eventi, colonne le
 * destinazioni, più la modalità di recapito.
 *
 * ## Tre cose che la tabella non deve far sembrare vere
 *
 * 1. **Una casella spenta su un canale non configurato non significa niente.**
 *    Se non hai collegato Telegram, quella colonna non è una scelta che stai
 *    facendo: è un canale che non esiste. La colonna resta visibile — nasconderla
 *    renderebbe la funzione invisibile invece che spiegata — ma è disattivata e
 *    dice perché.
 * 2. **L'email non ha il silenzioso.** Non è un caso non ancora implementato: il
 *    protocollo non ha il concetto. Mostrare un interruttore che non fa niente
 *    su quel canale sarebbe peggio che non mostrarlo.
 * 3. **Il push silenzioso non è garantito.** L'opzione esiste
 *    (`showNotification({ silent })`) ma alcune piattaforme la ignorano e
 *    suonano lo stesso. È detto una volta sotto la tabella, non su ogni riga.
 *
 * Il salvataggio manda **l'intera matrice**, non la casella toccata: è
 * idempotente, non lascia stati intermedi, e la pagina rimostra ciò che il
 * server ha riletto invece di ciò che sperava di aver salvato.
 */

import { Fragment, useEffect, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { Alert, Badge, Card, Spinner, TableWrap } from '@/ui';
import {
  useNotificationPreferences,
  useSavePreferences,
  type EventDefinition,
  type EventPreference,
  type NotificationChannelKind,
} from './api';

const CHANNELS: Array<{ key: NotificationChannelKind; label: string }> = [
  { key: 'telegram', label: 'Telegram' },
  { key: 'email', label: 'Email' },
  { key: 'push', label: 'Push' },
];

const GROUP_LABELS: Record<EventDefinition['group'], string> = {
  'auto-trading': 'Auto-trading',
  analisi: 'Analisi e resoconti',
  scoperta: 'Scoperta',
};

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Errore imprevisto';
}

export function PreferencesMatrix({
  /** I canali già collegati e verificati: gli altri non sono una scelta. */
  configured,
}: {
  configured: Record<NotificationChannelKind, boolean>;
}) {
  const query = useNotificationPreferences();
  const save = useSavePreferences();
  const [draft, setDraft] = useState<EventPreference[] | null>(null);

  // La bozza segue il server finché non la si tocca: senza, un salvataggio
  // altrove (o un ricaricamento) lascerebbe la tabella su valori vecchi.
  useEffect(() => {
    if (query.data) setDraft(query.data.items);
  }, [query.data]);

  if (query.isPending) return <Spinner label="Caricamento delle preferenze…" />;
  if (query.isError) {
    return (
      <Alert variant="error" title="Preferenze non disponibili">
        {errorMessage(query.error)}
      </Alert>
    );
  }

  const events = query.data?.events ?? [];
  const items = draft ?? [];

  const commit = (next: EventPreference[]) => {
    setDraft(next);
    save.mutate(next);
  };

  const toggleChannel = (eventType: string, channel: NotificationChannelKind) => {
    commit(
      items.map((item) =>
        item.eventType === eventType
          ? { ...item, channels: { ...item.channels, [channel]: !item.channels[channel] } }
          : item,
      ),
    );
  };

  const toggleSilent = (eventType: string) => {
    commit(
      items.map((item) => (item.eventType === eventType ? { ...item, silent: !item.silent } : item)),
    );
  };

  // Righe raggruppate: sei voci piatte non si leggono, e i gruppi dicono da
  // quale parte del sistema arriva l'avviso.
  const groups = [...new Set(events.map((event) => event.group))];

  return (
    <Card title="Quali avvisi, e dove" subtitle="Una riga per tipo di notifica, una colonna per destinazione">
      <TableWrap>
        <table className="table table--compact nprefs__table">
          <thead>
            <tr>
              <th>Notifica</th>
              {CHANNELS.map((channel) => (
                <th key={channel.key} className="nprefs__channel-head">
                  {channel.label}
                  {!configured[channel.key] && <span className="nprefs__note">non collegato</span>}
                </th>
              ))}
              <th className="nprefs__channel-head">Modalità</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              // La chiave va sul FRAMMENTO, non sulla prima riga dentro di
              // esso: `<>` non la accetta, e React ricostruirebbe il gruppo a
              // ogni render invece di riconoscerlo.
              <Fragment key={group}>
                <tr className="nprefs__group">
                  <th colSpan={CHANNELS.length + 2} scope="colgroup">
                    {GROUP_LABELS[group]}
                  </th>
                </tr>
                {events
                  .filter((event) => event.group === group)
                  .map((event) => {
                    const item = items.find((entry) => entry.eventType === event.eventType);
                    if (!item) return null;
                    return (
                      <tr key={event.eventType}>
                        <td>
                          <div className="nprefs__label">{event.label}</div>
                          <div className="nprefs__description">{event.description}</div>
                        </td>

                        {CHANNELS.map((channel) => (
                          <td key={channel.key} className="nprefs__cell">
                            <input
                              type="checkbox"
                              checked={item.channels[channel.key]}
                              disabled={!configured[channel.key] || save.isPending}
                              aria-label={`${event.label} su ${channel.label}`}
                              title={
                                configured[channel.key]
                                  ? undefined
                                  : `${channel.label} non è collegato: collegalo qui sotto`
                              }
                              onChange={() => toggleChannel(event.eventType, channel.key)}
                            />
                          </td>
                        ))}

                        <td className="nprefs__cell">
                          <button
                            type="button"
                            className="nprefs__mode"
                            aria-pressed={item.silent}
                            disabled={save.isPending}
                            onClick={() => toggleSilent(event.eventType)}
                          >
                            <Badge tone={item.silent ? 'neutral' : 'accent'}>
                              {item.silent ? '🔕 Silenziosa' : '🔔 Normale'}
                            </Badge>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </TableWrap>

      {save.isError && (
        <Alert variant="error" title="Preferenze non salvate">
          {errorMessage(save.error)}
        </Alert>
      )}

      <p className="nprefs__footnote">
        La modalità silenziosa recapita il messaggio senza suono né vibrazione.{' '}
        <strong>Telegram la rispetta sempre</strong>; sul push dipende dal dispositivo, che può
        ignorarla. L'email non ha il concetto e non ne è toccata.
      </p>
      <p className="nprefs__footnote">
        La notifica di prova non è in elenco di proposito: serve a verificare che un canale
        funzioni, e poterla spegnere renderebbe impossibile diagnosticare un canale muto.
      </p>
    </Card>
  );
}
